import * as Sentry from "@sentry/react";

/**
 * Production error telemetry.
 *
 * The Sentry DSN is a *publishable* value (it ships in the client bundle of
 * every site that uses Sentry), so it lives here as a constant rather than a
 * secret. Paste your project DSN below to switch telemetry on — while it is an
 * empty string, Sentry stays completely inert (no network, no overhead), so the
 * app behaves exactly as before.
 *
 * A per-session request ID is attached as a tag + breadcrumb so a user can
 * quote it from the ErrorBoundary fallback and we can find the exact event.
 */
const SENTRY_DSN =
  (import.meta.env["VITE_SENTRY_DSN"] as string | undefined) ||
  ""; // <-- paste https://<key>@<org>.ingest.sentry.io/<project> here

/**
 * Stable id for the whole browsing session; surfaced to users on crashes.
 *
 * Generated lazily on first use. The edge runtime forbids generating random
 * values in module (global) scope — doing it at import time made every SSR
 * route throw "Disallowed operation called within global scope" and return 500.
 */
let _sessionRequestId: string | undefined;

export function sessionRequestId(): string {
  if (!_sessionRequestId) {
    _sessionRequestId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);
  }
  return _sessionRequestId;
}

/**
 * Only report from the real published site. Preview sandboxes
 * (`id-preview--<uuid>.lovable.app`, any `--` host) and localhost are excluded
 * so test traffic never pollutes Sentry.
 */
function isProductionHost(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return false;
  if (host.includes("id-preview") || host.includes("--")) return false;
  return true;
}

let _initialized = false;

export function initSentry() {
  if (_initialized || !SENTRY_DSN || !isProductionHost()) return;
  _initialized = true;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: "production",
    release: (import.meta.env["VITE_APP_RELEASE"] as string | undefined) || "statlign@web",
    // Keep error reporting without browser tracing's fetch/history patches.
    tracesSampleRate: 0,
    // Trim noisy, non-actionable browser/extension errors.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications.",
      "Non-Error promise rejection captured",
    ],
  });

  Sentry.setTag("request_id", sessionRequestId());
  Sentry.addBreadcrumb({
    category: "session",
    level: "info",
    message: `Session started · request_id=${sessionRequestId()}`,
  });
}

/** True when telemetry is actually reporting (DSN set + production host). */
export function isSentryEnabled(): boolean {
  return _initialized;
}

/** Report an exception with a component/context breadcrumb. */
export function captureError(
  error: unknown,
  context?: Record<string, unknown>,
): string | undefined {
  if (!_initialized) return undefined;
  const captureContext = context
    ? { tags: { request_id: sessionRequestId() }, extra: context }
    : { tags: { request_id: sessionRequestId() } };
  return Sentry.captureException(error, captureContext);
}

/** Drop a navigation/action breadcrumb into the trail. */
export function addBreadcrumb(message: string, data?: Record<string, unknown>) {
  if (!_initialized) return;
  Sentry.addBreadcrumb(
    data
      ? { category: "app", level: "info", message, data }
      : { category: "app", level: "info", message },
  );
}
